import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";

import { cn } from "~/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full border font-medium whitespace-nowrap no-underline transition-colors duration-(--dur-fast) ease-out focus-visible:ring-4 focus-visible:ring-focus-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-45 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        secondary:
          "border-control-secondary-border bg-control-secondary text-control-secondary-fg hover:bg-control-secondary-hover hover:text-control-secondary-fg",
        primary:
          "border-control-primary bg-control-primary text-control-primary-fg hover:bg-control-primary-hover hover:text-control-primary-fg",
        accent:
          "border-control-accent bg-control-accent text-control-accent-fg hover:bg-control-accent-hover hover:text-control-accent-fg",
        danger:
          "border-status-danger-border bg-control-secondary text-status-danger-fg hover:bg-status-danger-bg hover:text-status-danger-fg",
        ghost:
          "border-transparent bg-transparent text-fg-muted hover:bg-surface-hover hover:text-fg-strong",
      },
      size: {
        default: "h-11 px-[22px] text-sm",
        sm: "h-9 px-4 text-label",
        block: "h-[52px] w-full px-[22px] text-base",
        icon: "size-9 p-0 [&_svg]:size-[18px]",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "default",
    },
  },
);

type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    pending?: boolean;
  };

export function Button({
  className,
  variant,
  size,
  asChild = false,
  pending = false,
  type = "button",
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...(asChild ? {} : { type })}
      disabled={disabled || pending}
      {...props}
    >
      {pending && !asChild ? "Bezig…" : children}
    </Comp>
  );
}
